import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetSpace, useUpdateProgress } from '@onesource/services';
import { useRef, useEffect } from 'react';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import { GcsUploadPage } from '#/features/community-hub/components/gcs-upload/gcs-upload-page';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/gcs-upload/$contentId',
)({
  component: GcsUploadContentRoute,
});

function GcsUploadContentRoute() {
  const { id: spaceId, moduleId, lessonId, contentId } = Route.useParams();
  const navigate = useNavigate();

  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  const backToLessonUrl = `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${lessonId}`;

  const block = lesson?.contentBlocks?.find(b => b.id === contentId && b.blockType === ContentBlockType.GCS_UPLOAD);

  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  useEffect(() => {
    if (!trackedRef.current && block) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: contentId });
    }
  }, [contentId, block, updateProgress]);

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="error">Upload block not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  if (!block.gcsBaseUrl) {
    return (
      <Box p={3}>
        <Alert severity="info">This upload block is not fully configured yet.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: backToLessonUrl })} sx={{ mt: 2 }}>
          Back to Lesson
        </Button>
      </Box>
    );
  }

  return (
    <>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel={block.title || 'Upload'}
      />
      <GcsUploadPage block={block} lessonId={lessonId} />
    </>
  );
}
